// Minimal typed fetcher for the gateway observability API.
//
// All requests go to the same-origin `/api` base by default, which in
// production is proxied server-side to the gateway by the Cloudflare Pages
// Function at functions/api/[[path]].js. The Function injects the
// GATEWAY_API_KEY Pages secret as `Authorization: Bearer`, so the frontend
// never holds or transmits the key. The frontend is therefore unauthenticated
// by design.
//
//   * Health is public and needs no auth.
//   * /admin/observability/* are gateway-key-gated, handled by the proxy.
//
// Failures are surfaced as typed errors so React Query and the UI can render
// explicit connection / non-data error states instead of fabricating metrics.

import { GATEWAY_API_BASE } from './env'
import type { UIMessage } from 'ai'
import type {
  HealthSnapshot,
  AggregatesResponse,
  EventsResponse,
  TokenBucket,
  TokenSeriesResponse,
  ModelsResponse,
} from '../types/gateway'

export type GatewayErrorCode =
  | 'http_error'
  | 'network_error'
  | 'parse_error'

export class GatewayError extends Error {
  code: GatewayErrorCode
  status?: number
  constructor(code: GatewayErrorCode, message: string, status?: number) {
    super(message)
    this.code = code
    this.status = status
    this.name = 'GatewayError'
  }
}

function authHeaders(): Record<string, string> {
  return { accept: 'application/json' }
}

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    throw new GatewayError(
      'parse_error',
      `non-JSON response (status ${response.status})`,
      response.status,
    )
  }
}

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${GATEWAY_API_BASE}${path}`
  let response: Response
  try {
    response = await fetch(url, { ...init, credentials: 'include', headers: { ...authHeaders(), ...(init?.headers || {}) } })
  } catch (cause) {
    throw new GatewayError(
      'network_error',
      cause instanceof Error ? cause.message : 'network error',
    )
  }
  if (!response.ok) {
    throw new GatewayError('http_error', `GET ${path} -> ${response.status}`, response.status)
  }
  return parseJson(response) as Promise<T>
}

export async function fetchHealth(): Promise<HealthSnapshot> {
  return fetchJson<HealthSnapshot>('/health')
}

export async function fetchModels(): Promise<ModelsResponse> {
  return fetchJson<ModelsResponse>('/v1/models')
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export async function streamChatCompletion(
  model: string,
  messages: ChatMessage[],
  onDelta: (text: string) => void,
  signal?: AbortSignal,
): Promise<void> {
  let response: Response
  try {
    response = await fetch(`${GATEWAY_API_BASE}/v1/chat/completions`, {
      method: 'POST',
      credentials: 'include',
      signal,
      headers: { accept: 'text/event-stream', 'content-type': 'application/json' },
      body: JSON.stringify({ model, messages, stream: true, max_tokens: 2048 }),
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new GatewayError('network_error', cause instanceof Error ? cause.message : 'network error')
  }
  if (!response.ok) {
    const data = await parseJson(response) as { error?: string; message?: string } | null
    throw new GatewayError('http_error', data?.message || data?.error || `POST /v1/chat/completions -> ${response.status}`, response.status)
  }
  if (!response.body) throw new GatewayError('network_error', 'stream body is unavailable')
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  const consume = (text: string) => {
    for (const line of text.split(/\r?\n/)) {
      if (!line.startsWith('data:')) continue
      const payload = line.slice(5).trim()
      if (!payload || payload === '[DONE]') continue
      const packet = JSON.parse(payload) as { choices?: Array<{ delta?: { content?: string } }>; error?: { message?: string } }
      if (packet.error?.message) throw new GatewayError('http_error', packet.error.message)
      const delta = packet.choices?.[0]?.delta?.content
      if (delta) onDelta(delta)
    }
  }
  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      buffer += decoder.decode()
      consume(buffer)
      break
    }
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split(/\r?\n/)
    buffer = lines.pop() || ''
    consume(lines.join('\n'))
  }
}

export interface AgentMessage extends ChatMessage {
  message_id: string
  status: string
}

export interface AgentMetadata {
  conversation_id: string
  model: string
}

export type AgentUIMessage = UIMessage<AgentMetadata>

export interface GatewayConversation {
  conversation_id: string
  title: string
  model: string
  created_at: string
  updated_at: string
}

export async function fetchConversations(): Promise<{ conversations: GatewayConversation[] }> {
  return fetchJson('/v1/conversations')
}

export async function fetchConversation(id: string): Promise<{ conversation: GatewayConversation; messages: AgentMessage[]; ui_messages: AgentUIMessage[] }> {
  return fetchJson(`/v1/conversations/${encodeURIComponent(id)}`)
}

export async function renameConversation(id: string, title: string): Promise<GatewayConversation> {
  return fetchJson(`/v1/conversations/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ title }) })
}

export async function deleteConversation(id: string): Promise<void> {
  const response = await fetch(`${GATEWAY_API_BASE}/v1/conversations/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include', headers: { accept: 'application/json' } })
  if (!response.ok) throw new GatewayError('http_error', `DELETE /v1/conversations/${id} -> ${response.status}`, response.status)
}

export async function approveAgentTool(conversationId: string, toolCallId: string): Promise<void> {
  const response = await fetch(`${GATEWAY_API_BASE}/v1/agent/approve`, {
    method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ conversation_id: conversationId, tool_call_id: toolCallId }),
  })
  if (!response.ok) throw new GatewayError('http_error', `POST /v1/agent/approve -> ${response.status}`, response.status)
}

export interface ModelHealthCheck {
  model: string
  protocol: string
  status: number | null
  outcome: 'ok' | 'error'
  duration_ms: number
  error_code: string | null
  checked_at: string
}

export interface ModelHealthSummary {
  model: string
  protocol: string
  checks: number
  successes: number
  avg_latency_ms: number
  last_checked_at: string
}

export interface ModelHealthResponse {
  window_hours: number
  latest: ModelHealthCheck[]
  summary: ModelHealthSummary[]
  history: ModelHealthCheck[]
}

export async function fetchModelHealth(): Promise<ModelHealthResponse> {
  return fetchJson<ModelHealthResponse>('/admin/model-health?hours=24')
}


export async function fetchAggregates(filters?: {
  from?: string | null
  to?: string | null
  route?: string | null
  model?: string | null
}): Promise<AggregatesResponse> {
  const params = new URLSearchParams()
  if (filters?.from) params.set('from', filters.from)
  if (filters?.to) params.set('to', filters.to)
  if (filters?.route) params.set('route', filters.route)
  if (filters?.model) params.set('model', filters.model)
  const qs = params.toString() ? `?${params.toString()}` : ''
  return fetchJson<AggregatesResponse>(`/admin/observability/aggregates${qs}`)
}

export async function fetchEvents(limit?: number): Promise<EventsResponse> {
  const q = typeof limit === 'number' && limit > 0 ? `?limit=${String(limit)}` : ''
  return fetchJson<EventsResponse>(`/admin/observability/events${q}`)
}

export async function fetchTokenSeries(filters?: {
  from?: string | null
  to?: string | null
  bucket?: TokenBucket
  route?: string | null
  model?: string | null
  channel?: string | null
}): Promise<TokenSeriesResponse> {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters || {})) {
    if (value) params.set(key, String(value))
  }
  const qs = params.toString() ? `?${params.toString()}` : ''
  return fetchJson<TokenSeriesResponse>(`/admin/observability/tokens${qs}`)
}
